<template>
  <div class="flex">
    <!-- Table Section -->
    <div :class="['overflow-x-auto transition-all', { 'w-full': !showPanel, 'w-2/3': showPanel }]">
      <table class="min-w-full bg-white border border-gray-200 rounded-md shadow">
        <thead class="bg-gray-50 border-b border-gray-200">
          <tr>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Facility Name</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Facility Contact</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Facility Email</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Services</th>
            <th class="py-3 px-4 text-left text-gray-600 font-semibold">Action</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
        <!-- Show loading row -->
        <tr v-if="isLoadingFacilities">
          <td colspan="5" class="text-center py-6 text-gray-500">
            <div class="flex justify-center items-center">
              <svg class="animate-spin h-5 w-5 text-orange-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor"
                  d="M4 12A8 12 0 018 4a8 8 0 0 1 8 8v0v-8C4 0 0 5.373 0 12h4zm2 5.291A7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3"></path>
              </svg>
              <span class="ml-2 text-gray-600">Loading facilities...</span>
            </div>
          </td>
        </tr>

        <!-- Show no data row -->
        <tr v-else-if="!paginatedFacilities.length">
          <td colspan="5" class="text-center py-6 text-gray-500">
            No facilities found.
          </td>
        </tr>

        <!-- Show facilities -->
        <tr v-else v-for="facility in paginatedFacilities" :key="facility.id" class="hover:bg-gray-50">
            <td class="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
              {{ facility.name || 'N/A' }}
            </td>
            <td class="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
              {{ facility.contact || 'N/A' }}
            </td>
            <td class="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
              {{ facility.email || 'N/A' }}
            </td>
            <td class="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
              {{ facility.services_count || 0 }}
            </td>
            <td class="px-6 py-2 whitespace-nowrap text-sm font-medium">
              <button @click="openPanel(facility)"
                class="bg-orange-400 hover:bg-orange-500 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200">
                View Details
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="flex justify-center items-center my-8">
        <div class="flex space-x-2">
          <button @click="goToPreviousPage" :disabled="currentPage === 1"
            class="text-blue-900 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed">
            Previous
          </button>
          <button v-for="page in visiblePages" :key="page" @click="goToPage(page)" :class="{
            'bg-blue-900 text-white': currentPage === page,
            'text-gray-600': currentPage !== page
          }" class="px-3 py-1 rounded-md hover:bg-blue-100">
            {{ page }}
          </button>
          <span v-if="showEllipsis" class="px-3 py-1 text-gray-600">...</span>
          <button v-if="showLastPage" @click="goToPage(totalPages)" :class="{
            'bg-blue-900 text-white': currentPage === totalPages,
            'text-gray-600': currentPage !== totalPages
          }" class="px-3 py-1 rounded-md hover:bg-blue-100">
            {{ totalPages }}
          </button>
          <button @click="goToNextPage" :disabled="currentPage === totalPages"
            class="text-blue-900 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed">
            Next
          </button>
        </div>
      </div>
    </div>

    <!-- Right Side Panel -->
    <div v-if="showPanel"
      class="w-1/3 bg-white px-2 border-l border-gray-200 transition-all flex flex-col max-h-[60vh]">
      <div class="flex justify-between items-center p-3 bg-gray-100 border-b border-gray-200">
        Facility Details
        <div class="flex items-center">
          <button @click="closePanel" class="text-gray-500 hover:text-gray-700 p-1">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 18" />
            </svg>
          </button>
        </div>
      </div>
      <div class="overflow-y-auto flex-grow">
        <div class="p-4">
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-xl font-bold">Facility Information</h2>
            <button @click="editFacility"
              class="bg-orange-400 hover:bg-orange-500 text-white px-3 py-1 rounded-md text-sm font-medium transition-colors duration-200">
              Edit
            </button>
          </div>
          <div v-if="isLoadingFacilityDetails" class="text-center py-4">Loading facility details...</div>
          <div v-else-if="errorMessage" class="text-red-500 text-center py-4">{{ errorMessage }}</div>
          <div v-else class="bg-white rounded-md border border-gray-200 p-5 mb-6">
            <div class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm">Facility Name:</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">{{ selectedFacility?.name || 'N/A' }}
              </div>
            </div>
            <div class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm">Phone:</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">{{ selectedFacility?.phone || 'N/A' }}
              </div>
            </div>
            <div class="mb-3 grid grid-cols-12 items-center">
              <label class="col-span-4 text-gray-600 text-sm">Email:</label>
              <div class="col-span-8 font-bold break-words whitespace-normal">{{ selectedFacility?.email || 'N/A' }}
              </div>
            </div>
          </div>

          <h2 class="text-xl font-bold mb-4">Services</h2>
          <div class="bg-white rounded-md border border-gray-200 p-5 mb-6">
            <div class="max-h-96 overflow-auto">
              <div v-if="isLoadingFacilityDetails" class="text-center py-4">Loading services...</div>
              <div v-else-if="!selectedFacility?.services?.length" class="text-center py-4">No services available.</div>
              <div v-else>
                <div class="grid grid-cols-3 gap-4 py-3 bg-gray-50 px-4 text-sm font-semibold text-gray-900">
                  <div>Service Name</div>
                  <div>Service Type</div>
                  <div class="text-right">Cost (KES)</div>
                </div>
                <div v-for="service in selectedFacility?.services || []" :key="service.id"
                  class="grid grid-cols-3 gap-4 py-3 px-4 border-b border-gray-200 text-sm">
                  <div class="text-gray-900">{{ service.name || 'N/A' }}</div>
                  <div class="text-gray-600">{{ service.type || 'N/A' }}</div>
                  <div class="text-right text-gray-900">{{ formatCurrency(service.cost) }}</div>
                </div>
              </div>
            </div>
          </div>

          <h2 class="text-xl font-bold mb-4">Specialists</h2>
          <div class="bg-white rounded-md border border-gray-200 p-5">
            <div class="max-h-96 overflow-auto">
              <div v-if="isLoadingFacilityDetails" class="text-center py-4">Loading specialists...</div>
              <div v-else-if="!selectedFacility?.specialists?.length" class="text-center py-4">No specialists available.</div>
              <div v-else>
                <div v-for="specialist in selectedFacility?.specialists || []" :key="specialist.id"
                  class="py-3 px-4 border-b border-gray-200 last:border-b-0">
                  <div class="flex justify-between items-start">
                    <div class="flex-1">
                      <div class="font-semibold text-gray-900">{{ specialist.name || 'N/A' }}</div>
                      <div class="text-sm text-gray-600">{{ specialist.email || 'N/A' }}</div>
                      <div class="text-sm text-gray-600">ID: {{ specialist.id_number || 'N/A' }}</div>
                    </div>
                    <div class="flex-1">
                      <div class="text-sm font-medium text-gray-700 mb-1">Services:</div>
                      <div class="flex flex-wrap gap-1">
                        <span v-for="service in specialist.services || []" :key="service.id"
                          class="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                          {{ service.name || 'N/A' }}
                        </span>
                        <span v-if="!specialist.services?.length" class="text-sm text-gray-500">No services assigned</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { FacilitiesAPI } from '@/services';
import { ref, computed, watch, onMounted } from 'vue';

const props = defineProps({
  searchQuery: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['edit-facility']);

const facilities = ref([]);
const currentPage = ref(1);
const facilitiesPerPage = 10;
const showPanel = ref(false);
const selectedFacility = ref(null);
const isLoadingFacilities = ref(false);
const isLoadingFacilityDetails = ref(false);
const errorMessage = ref(null);

const fetchFacilities = async () => {
  try {
    isLoadingFacilities.value = true;
    errorMessage.value = null;
    const response = await FacilitiesAPI.getAll();
    console.log('Full API Response:', JSON.stringify(response, null, 2));
    facilities.value = Array.isArray(response.data.data)
      ? response.data.data.map(facility => ({
          id: facility.id,
          name: facility.name,
          contact: facility.phone,
          phone: facility.phone,
          email: facility.email,
          services_count: facility.services_count,
          services: [],
          address: facility.address,
          created_at: facility.created_at,
          updated_at: facility.updated_at,
        }))
      : [];
    console.log('Facilities fetched:', facilities.value);
  } catch (error) {
    console.error('Error fetching facilities:', error, error.response);
    errorMessage.value = 'Failed to load facilities. Please try again.';
    facilities.value = [];
  } finally {
    isLoadingFacilities.value = false;
  }
};

const fetchFacility = async () => {
  try {
    isLoadingFacilityDetails.value = true;
    errorMessage.value = null;
    const response = await FacilitiesAPI.getOne(selectedFacility.value.id);
    console.log('Facility Details Response:', JSON.stringify(response, null, 2));
    if (response.data && response.data.data) {
      selectedFacility.value = {
        ...response.data.data,
        contact: response.data.data.phone,
        services: Array.isArray(response.data.data.specialized_services_with_cost)
          ? response.data.data.specialized_services_with_cost.map(service => ({
              id: service.id,
              name: service.name,
              type: service.service_type,
              cost: parseFloat(service.cost),
            }))
          : [],
        specialists: Array.isArray(response.data.data.specialists)
          ? response.data.data.specialists.map(specialist => ({
              id: specialist.id,
              name: specialist.name,
              email: specialist.email,
              id_number: specialist.id_number,
              services: Array.isArray(specialist.services)
                ? specialist.services.map(service => ({
                    id: service.id,
                    name: service.name,
                  }))
                : [],
            }))
          : [],
      };
      console.log('Selected Facility Details:', selectedFacility.value);
    } else {
      console.warn('No data found for facility:', selectedFacility.value.id);
      errorMessage.value = 'No details available for this facility.';
    }
  } catch (error) {
    errorMessage.value = error.response?.data?.message || 'Failed to load facility details.';
  } finally {
    isLoadingFacilityDetails.value = false;
  }
};

const editFacility = () => {
  if (selectedFacility.value) {
    emit('edit-facility', {
      id: selectedFacility.value.id,
      name: selectedFacility.value.name,
      phone: selectedFacility.value.phone,
      email: selectedFacility.value.email,
      address: selectedFacility.value.address,
      specialized_services_with_cost: selectedFacility.value.services,
      specialists: selectedFacility.value.specialists || [],
    });
    showPanel.value = false;
  }
};

onMounted(() => {
  console.log('FacilitiesTab mounted');
  fetchFacilities();
});

const filteredFacilities = computed(() => {
  if (!Array.isArray(facilities.value)) {
    return [];
  }
  console.log('Search Query:', props.searchQuery);
  if (!props.searchQuery) return facilities.value;
  const filtered = facilities.value.filter(facility =>
    facility.name?.toLowerCase().includes(props.searchQuery.toLowerCase()) ||
    facility.contact?.includes(props.searchQuery) ||
    facility.email?.toLowerCase().includes(props.searchQuery.toLowerCase())
  );
  return filtered;
});

const totalPages = computed(() => Math.ceil(filteredFacilities.value.length / facilitiesPerPage));

const paginatedFacilities = computed(() => {
  const start = (currentPage.value - 1) * facilitiesPerPage;
  const end = start + facilitiesPerPage;
  return filteredFacilities.value.slice(start, end);
});

const visiblePages = computed(() => {
  const pages = [];
  const current = currentPage.value;
  const total = totalPages.value;
  const range = 2;
  for (let i = Math.max(1, current - range); i <= Math.min(total, current + range); i++) {
    pages.push(i);
  }
  return pages;
});

const showEllipsis = computed(() => totalPages.value > 5 && currentPage.value < totalPages.value - 2);

const showLastPage = computed(() => totalPages.value > 5 && currentPage.value < totalPages.value - 2);

watch(() => props.searchQuery, () => {
  currentPage.value = 1;
});

const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
};

const goToPreviousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    console.log('Navigated to previous page:', currentPage.value);
  }
};

const goToNextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const openPanel = (facility) => {
  selectedFacility.value = facility;
  showPanel.value = true;
  console.log('Viewing details for:', facility.name);
  if (selectedFacility.value?.id) {
    fetchFacility();
  } else {
    console.warn('No valid facility ID for fetching details');
    errorMessage.value = 'Invalid facility selected.';
  }
};

const closePanel = () => {
  showPanel.value = false;
  selectedFacility.value = null;
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return 'N/A';
  return `KES ${Number(amount).toLocaleString('en-KE')}`;
};

// Expose fetchFacilities to parent component
defineExpose({ fetchFacilities });
</script>

<style>
.flex {
  display: flex;
}

.transition-all {
  transition: width 0.3s ease-in-out;
}

.bg-gray-50 {
  background-color: #f9fafb;
}

.overflow-x-auto {
  overflow-x: auto;
}
</style>